/**
 * i18n Mixin for Public Pages
 * 
 * Provides shared functionality for determining and setting the language
 * on public-facing pages (signup and appointment booking).
 * 
 * Usage:
 *   import { publicI18nMixin } from '@/mixins/publicI18nMixin'
 *   export default {
 *     mixins: [publicI18nMixin],
 *     // ... component code
 *   }
 */

import { getLocaleFromBackendCode, getLanguageDisplayName } from '@/setup/i18n'

export const publicI18nMixin = {
  data () {
    return {
      availableLanguages: [], // Will be populated with ward's supported languages
      currentLanguage: 'en' // Current selected language
    }
  },
  computed: {
    /**
     * Get language options for the dropdown
     * Format: [{ value: 'en', text: 'English' }, ...]
     */
    languageOptions () {
      return this.availableLanguages.map(locale => ({
        value: locale,
        text: getLanguageDisplayName(locale)
      }))
    }
  },
  methods: {
    /**
     * Initialize i18n based on ward settings and URL parameters
     * 
     * @param {Array} wardSupportedLanguages - Array of backend language codes (e.g., ['ENG', 'SPA'])
     * @param {Object} routeQuery - Vue Router query object (this.$route.query)
     * @returns {string} - Selected locale code (e.g., 'en', 'es', 'pt')
     */
    initializePublicI18n (wardSupportedLanguages = [], routeQuery = {}) {
      // Convert backend codes to locale codes
      const supportedLocales = wardSupportedLanguages
        .map(code => getLocaleFromBackendCode(code))
        .filter(locale => ['en', 'es', 'pt'].includes(locale))

      // Default to English if no supported languages configured
      if (supportedLocales.length === 0) {
        supportedLocales.push('en')
      }

      // Store available languages for dropdown
      this.availableLanguages = supportedLocales

      // Determine which language to use
      let selectedLocale = null

      // Priority 1: URL parameter (?lang=es)
      if (routeQuery.lang) {
        const requestedLang = routeQuery.lang.toLowerCase()
        // Validate that the requested language is supported by the ward
        if (supportedLocales.includes(requestedLang)) {
          selectedLocale = requestedLang
        }
      }

      // Priority 2: First language in ward's supported languages
      if (!selectedLocale) {
        selectedLocale = supportedLocales[0]
      }

      // Set the i18n locale
      this.$i18n.locale = selectedLocale
      this.currentLanguage = selectedLocale

      console.log('Public i18n initialized:', {
        wardSupported: wardSupportedLanguages,
        availableLocales: supportedLocales,
        urlParam: routeQuery.lang,
        selected: selectedLocale
      })

      return selectedLocale
    },

    /**
     * Change the current language
     * @param {string} locale - Locale code (e.g., 'en', 'es', 'pt')
     */
    changeLanguage (locale) {
      if (this.availableLanguages.includes(locale)) {
        this.$i18n.locale = locale
        this.currentLanguage = locale

        // Update URL parameter without reloading
        const query = { ...this.$route.query, lang: locale }
        this.$router.replace({ query }).catch(() => {})
      }
    },

    /**
     * Get translated appointment type description
     * Falls back to the description from API if no translation exists
     * 
     * @param {string} typeCode - Appointment type code (e.g., 'TITHING_SETTLEMENT')
     * @param {string} fallbackDescription - Description from API
     * @returns {string} - Translated or fallback description
     */
    getTranslatedAppointmentType (typeCode, fallbackDescription = '') {
      const key = `appointments.appointmentTypes.${typeCode}`
      const translated = this.$t(key)
      
      // If translation key is returned as-is, use fallback
      if (translated === key || translated.includes('[TRANSLATE]')) {
        return fallbackDescription || typeCode
      }
      
      return translated
    },

    /**
     * Get translated error message based on error response
     * Maps backend error codes/messages to translated messages
     * 
     * @param {Object} error - Axios error object
     * @param {string} defaultKey - Default translation key
     * @returns {string} - Translated error message
     */
    getTranslatedError (error, defaultKey = 'appointments.errors.processingRequest') {
      if (!error.response) {
        return this.$t(defaultKey)
      }

      const status = error.response.status
      const detail = error.response.data?.detail || ''

      // Map common errors
      const errorMap = {
        403: 'appointments.errors.forbidden',
        400: 'appointments.errors.invalidType',
        409: 'appointments.errors.conflict'
      }

      // Check for specific error messages
      if (detail.includes('disabled')) {
        return this.$t('appointments.errors.disabled')
      }
      if (detail.includes('Forbidden')) {
        return this.$t('appointments.errors.forbidden')
      }
      if (detail.includes('Invalid')) {
        return this.$t('appointments.errors.invalidType')
      }
      if (detail.includes('Conflict') || detail.includes('already booked')) {
        return this.$t('appointments.errors.conflict')
      }

      // Use status code mapping
      if (errorMap[status]) {
        return this.$t(errorMap[status])
      }

      // Fallback to default
      return this.$t(defaultKey)
    }
  }
}

